import React, { useMemo } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useReports from "../hooks/useReports";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { UIState } from "../types/ui-types";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { Report, ReportStatus } from "../api/models/report.models";
import ContainerHeader from "../components/containerHeader";
import AllReportsCounter from "../components/allReportsCounter";
import ReportsTable from "../views/reportsTable";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useNavigate } from "react-router";
import InvoiceTable from "../views/invoiceTable";
import FileUploadDialog from "../dialogs/fileUploadDialog";
import ChooseYearDialog from "../dialogs/chooseYearDialog";
import PpwrNoticeDialog from "../dialogs/ppwrNoticeDialog";
import useCompany from "../hooks/useCompany";
import { Tooltip } from "primereact/tooltip";
import { InfoDot } from "../components/infoDot";
import { useAuth } from "../contexts/authContext";

const AllReportsContainer = styled.div`
  position: relative;
`;

const PaddedContainer = styled.div`
  padding: 0rem 2rem 6rem 2rem;
  text-align: left;

  & .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
    background-color: var(--sumi-header-background);
    color: #333;
  }

  & .p-tabview .p-tabview-nav li .p-tabview-nav-link {
    border-radius: 0;
  }
`;

const TabPanelDescriptionHeader = styled.h3`
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 1rem;
  margin-top: 2rem;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;

  > div {
    margin: auto 0;
  }
`;

const TabPanelDescription = styled.p`
  font-size: 0.9rem;
  margin-bottom: 2rem;
  color: var(--text-color-secondary);
  max-width: 600px;
`;

const ReportActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: nowrap;

  .p-inputtext {
    min-width: 300px;
  }

  .p-button {
    white-space: nowrap;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  & .info-button {
    margin-top: 38px;
  }
`;

const AllReports: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const showSentReports = searchParams.get("showSentReports") === "true";
  const { canCreateReportsForSelectedCompanyOrOrganization } = useAuth();
  const [activeIndex, setActiveIndex] = React.useState<number>(
    showSentReports ? 1 : 0,
  );
  const [companyId, organizationId, companyName, organizationName] = useUIStore(
    useShallow((state: UIState) => [
      state.companyId,
      state.organizationId,
      state.companyName,
      state.organizationName,
    ]),
  );

  const { company, isLoadingCompany } = useCompany({
    companyId,
    enabled: true,
  });

  const [showImportExcelDialog, setShowImportExcelDialog] =
    React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState<number | null>(null);
  const [selectedQuarter, setSelectedQuarter] = React.useState<number | null>(
    null,
  );
  const [selectedMonth, setSelectedMonth] = React.useState<number | null>(null);
  const [showYearDialog, setShowYearDialog] = React.useState(false);
  const [showPpwrNoticeDialog, setShowPpwrNoticeDialog] =
    React.useState(false);

  const { reportsData, isLoadingReports } = useReports({
    companyId,
    organizationId,
  });

  const numberOfDraftReports = React.useMemo(() => {
    return reportsData?.filter(
      (report) => report.reportStatus === ReportStatus.Draft,
    ).length;
  }, [reportsData]);

  const reportForThisYearCreated = React.useMemo(() => {
    const report = reportsData?.find(
      (report) => report.year === new Date().getFullYear(),
    );
    return !!report;
  }, [reportsData]);

  const sortedReportsData = React.useMemo(() => {
    return reportsData?.sort((a, b) => {
      // First sort by year (descending - biggest year first)
      const yearDiff = (b.year || 0) - (a.year || 0);
      if (yearDiff !== 0) return yearDiff;

      // If years are equal, sort by quarter (ascending)
      const quarterDiff = (a.quarter || 0) - (b.quarter || 0);
      if (quarterDiff !== 0) return quarterDiff;

      // If years and quarters are equal, sort by month (ascending)
      return (a.month || 0) - (b.month || 0);
    });
  }, [reportsData]);

  const otherReports = React.useMemo(() => {
    return sortedReportsData?.filter(
      (report) => report.reportStatus !== ReportStatus.Sent,
    );
  }, [sortedReportsData]);

  const sentReports = React.useMemo(() => {
    return sortedReportsData?.filter(
      (report) => report.reportStatus === ReportStatus.Sent,
    );
  }, [sortedReportsData]);

  const navigateToReportingChoice = () => {
    navigate(
      `/reporting-choice?&companyId=${companyId}&organizationId=${organizationId}`,
    );
  };

  const handleNewReportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (new Date().getFullYear() === 2026) {
      setShowPpwrNoticeDialog(true);
      return;
    }

    navigateToReportingChoice();
  };

  const createNewDisabled = useMemo(() => {
    if (!organizationId && company?.organizations?.length) {
      return true;
    }

    return false;
  }, [company, organizationId]);

  return (
    <AllReportsContainer>
      {showImportExcelDialog && (
        <FileUploadDialog
          show={showImportExcelDialog}
          onHide={() => setShowImportExcelDialog(false)}
          onAccept={() => {
            console.log("Accepted");
          }}
          reportId={undefined}
          year={selectedYear!}
          quarter={selectedQuarter!}
          month={selectedMonth!}
        />
      )}

      {showPpwrNoticeDialog && (
        <PpwrNoticeDialog
          show={showPpwrNoticeDialog}
          onHide={() => setShowPpwrNoticeDialog(false)}
          onAccept={() => {
            setShowPpwrNoticeDialog(false);
            navigateToReportingChoice();
          }}
        />
      )}

      {showYearDialog && (
        <ChooseYearDialog
          show={showYearDialog}
          onHide={() => setShowYearDialog(false)}
          onSelect={(year, quarter, month) => {
            setSelectedYear(year);
            setSelectedQuarter(quarter);
            setSelectedMonth(month);
            setShowImportExcelDialog(true);
          }}
          existingReports={reportsData}
        />
      )}

      <ContainerHeader
        description={t("subheader-all-reports")}
        title={t("header-all-reports")}
        rightSideContent={
          <AllReportsCounter
            late={0}
            notCreated={reportForThisYearCreated ? 0 : 1}
            inDraft={numberOfDraftReports || 0}
          />
        }
      />
      <PaddedContainer className="card">
        <LoadingContainer
          status={
            isLoadingReports || isLoadingCompany
              ? HttpRequestStatus.LOADING
              : HttpRequestStatus.ERROR
          }
        >
          <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}
          >
            <TabPanel header={t("header-reports")}>
              <HeaderContainer>
                <InfoDot
                  position="right"
                  text={t("text-reporting-info")}
                  marginLeft="10px"
                  marginTop="15px"
                >
                  <TabPanelDescriptionHeader>
                    {organizationId ? (
                      <span>
                        {t("text-reporting-tab-description-organization", {
                          organization: organizationName,
                        })}
                      </span>
                    ) : (
                      <span>
                        {t("text-reporting-tab-description-company", {
                          company: companyName,
                        })}
                      </span>
                    )}
                  </TabPanelDescriptionHeader>
                </InfoDot>
              </HeaderContainer>
              <TabPanelDescription>
                {t("text-reporting-tab-subdescription")}
              </TabPanelDescription>
              <ReportActions>
                <InputText placeholder={t("placeholder-search-report")} />
                {createNewDisabled && (
                  <Tooltip target=".button-wrapper" position="top" />
                )}
                <div
                  className="button-wrapper"
                  data-pr-tooltip={t(
                    "tooltip-cannot-create-report-no-organization",
                  )}
                >
                  <Button
                    id="create-new-report-button"
                    label={t("cmd-create-new-product")}
                    onClick={handleNewReportClick}
                    disabled={
                      createNewDisabled ||
                      !canCreateReportsForSelectedCompanyOrOrganization()
                    }
                  />
                </div>

                {/* <Button
                  type="button"
                  label={t("cmd-load-from-file")}
                  onClick={() => setShowYearDialog(true)}
                  style={{ marginBottom: "auto" }}
                  outlined
                  disabled={!isReportCreator}
                /> */}
              </ReportActions>
              {!!otherReports?.length && (
                <ReportsTable
                  reports={otherReports}
                  organizations={company?.organizations}
                  company={company}
                  groupRowsBy="yearQuarterMonth"
                  allReports={reportsData}
                />
              )}
            </TabPanel>

            <TabPanel header={t("header-sent-reports")}>
              <HeaderContainer>
                <InfoDot
                  position="right"
                  text={t("text-sent-reports-info")}
                  marginLeft="10px"
                  marginTop="15px"
                >
                  <TabPanelDescriptionHeader>
                    <span>
                      {organizationName
                        ? t("text-sent-reports-tab-description-organization", {
                            organization: organizationName,
                          })
                        : t("text-sent-reports-tab-description-company", {
                            company: companyName,
                          })}
                    </span>
                  </TabPanelDescriptionHeader>
                </InfoDot>
              </HeaderContainer>
              <TabPanelDescription>
                {t("text-sent-reports-tab-subdescription")}
              </TabPanelDescription>

              {!!sentReports?.length && (
                <ReportsTable
                  reports={sentReports}
                  groupRowsBy="yearQuarterMonth"
                  allReports={reportsData}
                  company={company}
                  organizations={company?.organizations}
                />
              )}
            </TabPanel>
            <TabPanel header={t("header-invoicing")}>
              <HeaderContainer>
                <InfoDot
                  position="right"
                  text={t("text-invoicing-tab-info")}
                  marginLeft="10px"
                  marginTop="15px"
                >
                  <TabPanelDescriptionHeader>
                    <span>
                      {organizationName
                        ? t("text-invoicing-tab-description-organization", {
                            organization: organizationName,
                          })
                        : t("text-invoicing-tab-description-company", {
                            company: companyName,
                          })}
                    </span>
                  </TabPanelDescriptionHeader>
                </InfoDot>
              </HeaderContainer>
              <TabPanelDescription>
                {t("text-invoicing-tab-subdescription")}
              </TabPanelDescription>
              <InvoiceTable />
            </TabPanel>
          </TabView>
        </LoadingContainer>
      </PaddedContainer>
    </AllReportsContainer>
  );
};

export default AllReports;
